import { Link, router } from '@inertiajs/react'
import { admin_imports_path, data_admin_import_path } from '@/lib/routes'
import { useState } from 'react'
import AppLayout from '@/components/AppLayout'
import PageTitle from '@/components/PageTitle'
import SourceLink from './components/SourceLink'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table'

interface Pagination {
  page: number
  perPage: number
  totalRows: number
  totalPages: number
}

interface Props {
  dataSource: { id: string; name: string; source_type: string; source_identifier: string; source_url: string | null; target_model: string }
  columns: string[]
  rows: Record<string, unknown>[]
  pagination: Pagination
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function TruncatedCell({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = value.length > 80

  if (!isLong) {
    return <span className="text-sm">{value}</span>
  }

  return (
    <div className="relative group">
      {expanded ? (
        <div>
          <span className="text-sm whitespace-pre-wrap break-words">{value}</span>
          <Button
            type="button"
            variant="link"
            onClick={(e) => { e.stopPropagation(); setExpanded(false) }}
            className="ml-1 h-auto p-0 text-xs"
          >
            less
          </Button>
        </div>
      ) : (
        <div className="flex items-baseline gap-1">
          <span className="text-sm truncate block max-w-[300px]">{value}</span>
          <Button
            type="button"
            variant="link"
            onClick={(e) => { e.stopPropagation(); setExpanded(true) }}
            className="h-auto p-0 text-xs shrink-0"
          >
            more
          </Button>
        </div>
      )}
    </div>
  )
}

export default function DataShow({ dataSource, columns, rows, pagination }: Props) {
  const goToPage = (page: number) => {
    router.get(data_admin_import_path(dataSource.id), { page }, { preserveState: true })
  }

  return (
    <AppLayout header={<PageTitle title={dataSource.target_model} subtitle={`${dataSource.name} · ${pagination.totalRows.toLocaleString()} rows`}
      subtitleExtra={<SourceLink ds={dataSource} />} />}>
      <div className="flex flex-col gap-4">
        <div>
          <Link href={admin_imports_path()} className="text-sm text-muted-foreground hover:text-foreground">
            &larr; Back to Data Sources
          </Link>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((col) => (
                      <TableHead key={col} className="whitespace-nowrap">
                        {col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx}>
                      {columns.map((col) => (
                        <TableCell key={col}>
                          <TruncatedCell value={formatCellValue(row[col])} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages} ({pagination.totalRows.toLocaleString()} total rows)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
